import React from 'react';
import styled from 'styled-components';
import { getIcon } from '../../../utils';
import { AnchorText, Col, Container, Grid, Section } from '../../common';
import { NavbarPrimaryLogo } from './NavbarPrimaryLogo';

const StyledSpan = styled.span`
  color: ${(props) => props.$color};
  &:hover {
    color: ${(props) => props.$colorHover};
  }
`;

const StyledIcon = styled.span`
  svg {
    color: ${(props) => props.$color};
    &:hover {
      color: ${(props) => props.$colorHover};
    }
  }
`;

const FooterLinks = ({ page, sanity }) => {
  // Destructure for clarity
  const { business, colors, pages, footer, services } = sanity;
  const { gbp } = page;

  // Build physical address
  const physicalAddress = [
    gbp?.address,
    gbp?.city,
    gbp?.stateShort && gbp?.stateShort,
    gbp?.zip,
  ]
    .filter(Boolean)
    .join(', ');

  // Define contact info array
  const contact = [
    {
      id: 1,
      icon: 'Phone',
      anchor: gbp?.phone,
      url: `tel:${gbp?.phone}`,
    },
    {
      id: 2,
      icon: 'Email',
      anchor: gbp?.email,
      url: `mailto:${gbp?.email}`,
    },
    {
      id: 3,
      icon: 'Address',
      anchor: physicalAddress,
      url: gbp?.gbpUrl,
    },
    {
      id: 4,
      icon: 'Hours',
      anchor: gbp?.hoursWeek,
      url: gbp?.gbpUrl,
    },
  ];

  // Define socials list
  const socials = [
    { id: 1, icon: 'Facebook', url: business?.facebookUrl },
    { id: 2, icon: 'Instagram', url: business?.instagramUrl },
    { id: 3, icon: 'Twitter', url: business?.twitterUrl },
    { id: 4, icon: 'LinkedIn', url: business?.linkedInUrl },
    { id: 5, icon: 'YouTube', url: business?.youTubeUrl },
    { id: 6, icon: 'TikTok', url: business?.tikTokUrl },
    { id: 7, icon: 'Yelp', url: business?.yelpUrl },
  ];

  // Helpers for rendering service pages
  const renderServicePages = () => (
    <ul className="mt-4 space-y-4">
      {pages
        ?.sort((a, b) => a?.order - b?.order)
        ?.filter((p) => p?.type === 'service' && p?.addToFooter)
        .map((p) => (
          <li key={p?.slug?.current}>
            <AnchorText
              sanity={sanity}
              type="internal"
              color={colors?.neutral?.default}
              colorHover={colors?.accent?.default}
              to={p?.slug?.current}
              className="text-base break-words"
            >
              <span className="text-sm break-words">{p?.name}</span>
            </AnchorText>
          </li>
        ))}
    </ul>
  );

  // Helpers for rendering service list (from services array)
  const renderServiceList = () => (
    <ul className="mt-4 space-y-4">
      {services
        ?.sort((a, b) => a?.order - b?.order)
        .map((service) => (
          <li key={service?.slug?.current}>
            <AnchorText
              sanity={sanity}
              type="internal"
              color={colors?.neutral?.default}
              colorHover={colors?.accent?.default}
              to={service?.link?.slug?.current}
              className="text-base break-words"
            >
              <span className="text-sm break-words">{service?.name}</span>
            </AnchorText>
          </li>
        ))}
    </ul>
  );

  return (
    <Section
      idName="footer-links"
      padding="sm"
      bgColor={colors?.neutral?.lighter}
    >
      <Container padding="sm" classes="py-12 lg:py-16">
        {/* Use a custom grid: 4 columns with the first column twice as wide */}
        <Grid classes="grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_1fr] gap-12">
          {/* Column 1: Logo, Slogan & Social Icons */}
          <Col classes="flex flex-col space-y-6 justify-center text-center lg:justify-start">
            <NavbarPrimaryLogo sanity={sanity} />
            <p className="max-w-xs mx-auto text-base italic text-gray-500">
              {gbp?.slogan}
            </p>
            <ul className="flex justify-center mt-12 space-x-6">
              {socials?.map((social) =>
                social?.url ? (
                  <li key={social?.icon}>
                    <AnchorText
                      sanity={sanity}
                      type="external"
                      color={colors?.neutral?.light}
                      colorHover={colors?.accent?.light}
                      href={social?.url}
                    >
                      <span className="sr-only">{social?.icon}</span>
                      <StyledIcon
                        $color={colors?.accent?.default}
                        $colorHover={colors?.accent?.dark}
                        className="!text-3xl"
                      >
                        {React.createElement(getIcon(social?.icon))}
                      </StyledIcon>
                    </AnchorText>
                  </li>
                ) : null
              )}
            </ul>
          </Col>
          {/* Column 2: Company */}
          <Col classes="text-center lg:text-left">
            <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
              Our Company
            </h4>
            <ul className="mt-4 space-y-4">
              {pages
                ?.sort((a, b) => a?.order - b?.order)
                ?.filter((p) => p?.type === 'brand' && p?.addToFooter)
                .map((p) => (
                  <li key={p?.slug?.current}>
                    <AnchorText
                      sanity={sanity}
                      type="internal"
                      color={colors?.neutral?.default}
                      colorHover={colors?.accent?.default}
                      to={p?.slug?.current}
                      className="text-base break-words"
                    >
                      <span className="text-sm break-words">{p?.name}</span>
                    </AnchorText>
                  </li>
                ))}
            </ul>
          </Col>
          {/* Column 3: Our Services */}
          <Col classes="text-center lg:text-left">
            <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
              Our Services
            </h4>
            {footer?.serviceListSource === 'serviceList'
              ? renderServiceList()
              : renderServicePages()}
          </Col>
          {/* Column 4: Get In Touch */}
          <Col classes="text-center lg:text-left">
            <h4 className="font-semibold tracking-tight uppercase text-md text-zinc-800">
              Get In Touch
            </h4>
            <ul className="mt-4">
              {contact?.map((item) => (
                <li key={item?.id} className="pt-4 first:pt-0">
                  <AnchorText
                    sanity={sanity}
                    type="external"
                    color={colors?.neutral?.default}
                    colorHover={colors?.accent?.default}
                    href={item?.url}
                    className="text-base break-words"
                  >
                    <span className="text-sm break-words">{item?.anchor}</span>
                  </AnchorText>
                </li>
              ))}
            </ul>
          </Col>
        </Grid>
      </Container>
    </Section>
  );
};

export { FooterLinks };
